import React from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import PrintDialog from '@/components/shared/PrintDialog';
import { mapProductionOrderDetail, type ProductionOrderApi } from '@/lib/transformers/production-orders';
import { Loader2 } from 'lucide-react';

interface ProductionOrderPrintModalProps {
    orderIds?: string[];
    onClose: () => void;
    overrideData?: any;
}

export function ProductionOrderPrintModal({ orderIds = [], onClose, overrideData }: ProductionOrderPrintModalProps) {
    const idsToFetch = orderIds.length > 0 ? orderIds : [];

    const queries = useQueries({
        queries: idsToFetch.map(id => ({
            queryKey: ['production-order', id],
            queryFn: async () => {
                const raw = await trpcClient.productionOrders.getById.query({ id }) as ProductionOrderApi;
                return mapProductionOrderDetail(raw);
            },
            enabled: !overrideData && idsToFetch.length > 0,
        })),
    });

    const isLoading = queries.some(q => q.isLoading);
    const lsxDataList = queries.map(q => q.data).filter(Boolean);
    const effectiveLsxList = lsxDataList.length > 0 ? lsxDataList : (overrideData ? [overrideData] : []);

    const salesOrderIds = Array.from(new Set(effectiveLsxList.map((d: any) => d?.salesOrderId).filter(Boolean)));
    const soQueries = useQueries({
        queries: salesOrderIds.map(soId => ({
            queryKey: ['order-full', soId],
            queryFn: async () => {
                try {
                    const order = await trpcClient.orders.getById.query({ id: soId as string }) as any;
                    return {
                        id: soId,
                        items: order?.items || order?.orderDetails || [],
                        salesOrder: order,
                    };
                } catch { return { id: soId, items: [], salesOrder: null }; }
            },
        })),
    });

    const isSoLoading = soQueries.some(q => q.isLoading);
    const soDataList = soQueries.map(q => q.data).filter(Boolean);

    const isReady = overrideData ? true : (!isLoading && !isSoLoading);
    
    if (orderIds.length === 0 && !overrideData) {
        return null;
    }

    if (!isReady && idsToFetch.length > 0 && !overrideData) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
                    <Loader2 className="w-8 h-8 animate-spin mb-4 text-primary" />
                    <p className="text-slate-600 font-medium">Đang tải dữ liệu lệnh sản xuất...</p>
                </div>
            </div>
        );
    }

    const printData = effectiveLsxList.map((lsxData: any) => {
        const soData = soDataList.find(so => so?.id === lsxData.salesOrderId);
        const orderProducts = soData?.items || lsxData?.orderProducts || [];
        const salesOrder = soData?.salesOrder || lsxData?.salesOrder;
        
        return {
            lsx: { ...lsxData, ...overrideData },
            salesOrder,
            orderProducts
        };
    });

    return (
        <PrintDialog
            open={true}
            onClose={onClose}
            type="lsx"
            data={printData}
            orientation="portrait"
        />
    );
}
